import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import LabelText from 'Components/LabelText';
import IconSvg from 'Components/IconSvg';

const InputField = ({
  id,
  name,
  label,
  value: propValue,
  onChange,
  isValid,
  type,
  failureMessage,
  placeholder,
  required,
  className,
  disabled,
  showText,
  hideText,
  styledMessage,
  autoFocus,
}) => {
  const [revealed, setRevealed] = useState(type === 'text' || type === 'email');
  const [value, setValue] = useState(propValue);
  const [message, setMessage] = useState('');
  const inputEl = useRef();
  const error = failureMessage !== '';

  // Scroll input into view if autoFocus
  useEffect(() => {
    if (inputEl.current && autoFocus) {
      inputEl.current.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [autoFocus]);

  // Set initial value based on prop value
  useEffect(() => {
    setValue(propValue);
  }, [propValue]);

  const handleChange = (event) => {
    const value = event.target.value;
    const { message } = isValid(value, event);
    setValue(value);
    setMessage(message);
    onChange(event);
  };

  const toggleShowHide = () => {
    setRevealed((prevRevealed) => !prevRevealed);
  };

  const spaceCheck = (e) => {
    if (e.which === 32) e.preventDefault();
  };

  return (
    <div
      className={
        error ? 'input__container input__container--error' : 'input__container'
      }
    >
      <label htmlFor={id}>
        <LabelText
          label={label}
          message={message}
          failureMessage={failureMessage}
          styledMessage={styledMessage}
        />
      </label>
      <input
        id={id}
        name={name}
        type={revealed ? (type === 'password' ? 'text' : type) : 'password'}
        value={value}
        onKeyPress={spaceCheck}
        onChange={handleChange}
        onKeyUp={handleChange}
        placeholder={placeholder}
        required={required}
        className={className}
        maxLength="100"
        autoFocus={autoFocus}
        ref={inputEl}
        disabled={disabled}
      />
      {type !== 'text' && type !== 'email' && (
        <span className="input__link" role="button" onClick={toggleShowHide}>
          {revealed ? (
            <IconSvg type="previewOff" title={hideText} />
          ) : (
            <IconSvg type="preview" title={showText} />
          )}
        </span>
      )}
    </div>
  );
};

InputField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  label: PropTypes.string.isRequired,
  labelClassName: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  isValid: PropTypes.func,
  type: PropTypes.string,
  failureMessage: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  showText: PropTypes.string,
  hideText: PropTypes.string,
  styledMessage: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

InputField.defaultProps = {
  isValid: () => ({ message: '' }),
  type: 'text',
  name: null,
  value: '',
  failureMessage: '',
  placeholder: null,
  required: null,
  className: null,
  showText: 'Show',
  hideText: 'Hide',
  styledMessage: false,
  autoFocus: false,
};

export default InputField;
